import React from 'react';

/**
 * Inline SVG fallback patterns for events that have no uploaded cover image.
 *
 * Each pattern uses CSS custom properties `--event-primary` and
 * `--event-secondary` so the event's brand colors come through. They are
 * deterministically picked by hashing the event id, so the same event
 * always gets the same pattern.
 *
 * Colors are applied via inline `style` rather than the `fill` attribute
 * because the SVG `var()` function only resolves inside CSS properties,
 * not inside presentation attributes.
 */

const SVG_PROPS = {
  viewBox: '0 0 400 400',
  preserveAspectRatio: 'xMidYMid slice',
  'aria-hidden': true,
  width: '100%',
  height: '100%',
} as const;

const PRIMARY_STYLE: React.CSSProperties = {
  fill: 'var(--event-primary, var(--orange-primary))',
};

const SECONDARY_STYLE: React.CSSProperties = {
  fill: 'var(--event-secondary, var(--orange-hover))',
};

const SECONDARY_STROKE_STYLE: React.CSSProperties = {
  stroke: 'var(--event-secondary, var(--orange-hover))',
  fill: 'none',
};

const Stripes: React.FC = () => (
  <svg {...SVG_PROPS}>
    <rect width="400" height="400" style={PRIMARY_STYLE} />
    <g style={SECONDARY_STYLE} opacity="0.55">
      <path d="M-50,0 L130,0 L320,400 L140,400 Z" />
      <path d="M230,0 L410,0 L600,400 L420,400 Z" />
    </g>
  </svg>
);

const Arcs: React.FC = () => (
  <svg {...SVG_PROPS}>
    <rect width="400" height="400" style={PRIMARY_STYLE} />
    <g style={SECONDARY_STROKE_STYLE} strokeWidth="32" opacity="0.55">
      <circle cx="0" cy="0" r="140" />
      <circle cx="0" cy="0" r="240" />
      <circle cx="0" cy="0" r="340" />
      <circle cx="0" cy="0" r="440" />
    </g>
  </svg>
);

const Dots: React.FC = () => (
  <svg {...SVG_PROPS}>
    <rect width="400" height="400" style={PRIMARY_STYLE} />
    <g style={SECONDARY_STYLE} opacity="0.6">
      <circle cx="70" cy="60" r="20" />
      <circle cx="180" cy="120" r="36" />
      <circle cx="320" cy="80" r="14" />
      <circle cx="120" cy="220" r="26" />
      <circle cx="280" cy="200" r="44" />
      <circle cx="60" cy="340" r="22" />
      <circle cx="220" cy="320" r="18" />
      <circle cx="340" cy="280" r="30" />
    </g>
  </svg>
);

const Waves: React.FC = () => (
  <svg {...SVG_PROPS}>
    <rect width="400" height="400" style={PRIMARY_STYLE} />
    <g style={SECONDARY_STYLE} opacity="0.55">
      <path d="M-20,90 Q100,30 220,90 T440,90 L440,150 Q320,210 200,150 T-20,150 Z" />
      <path d="M-20,240 Q100,180 220,240 T440,240 L440,300 Q320,360 200,300 T-20,300 Z" />
    </g>
  </svg>
);

const Triangles: React.FC = () => (
  <svg {...SVG_PROPS}>
    <rect width="400" height="400" style={PRIMARY_STYLE} />
    <g style={SECONDARY_STYLE} opacity="0.55">
      <polygon points="0,0 200,0 100,160" />
      <polygon points="200,0 400,0 300,160" />
      <polygon points="100,160 300,160 200,320" />
      <polygon points="0,260 200,260 100,420" />
      <polygon points="200,260 400,260 300,420" />
    </g>
  </svg>
);

const Blobs: React.FC = () => (
  <svg {...SVG_PROPS}>
    <rect width="400" height="400" style={PRIMARY_STYLE} />
    <g style={SECONDARY_STYLE} opacity="0.55">
      <ellipse cx="60" cy="80" rx="130" ry="95" transform="rotate(-20 60 80)" />
      <ellipse cx="330" cy="200" rx="150" ry="105" transform="rotate(30 330 200)" />
      <ellipse cx="190" cy="350" rx="115" ry="85" transform="rotate(-15 190 350)" />
    </g>
  </svg>
);

const PATTERNS: React.FC[] = [Stripes, Arcs, Dots, Waves, Triangles, Blobs];

const hashString = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

interface EventFallbackPatternProps {
  seed: string;
}

const EventFallbackPattern: React.FC<EventFallbackPatternProps> = ({ seed }) => {
  const Pattern = PATTERNS[hashString(seed) % PATTERNS.length];
  return <Pattern />;
};

export default EventFallbackPattern;
